import type { Team, TeamScore, Vec3 } from '../types';
import type { LoopAudioDefinition } from '../audio/GameSfxDefinition';
import { MEDIA_KEYS, mediaAssetUrl } from '../content/MediaUrls';
import type { TeamColors } from './TeamPresentation';

export interface ArenaMatchPresentation {
  readonly score: TeamScore;
  readonly teamColors: TeamColors;
}

export interface ArenaTextureSpec {
  readonly url: string;
  readonly repeatX: number;
  readonly repeatY: number;
}

export interface ArenaEnvironmentPresentation {
  readonly backgroundColor: number;
  readonly fogColor: number;
  readonly fogDensity: number;
  readonly exposure: number;
  readonly skyTopColor: number;
  readonly skyHorizonColor: number;
  readonly skyBottomColor: number;
  readonly sun?: Readonly<{
    direction: Vec3;
    color: number;
    coreSize: number;
    haloSize: number;
    intensity: number;
  }>;
}

export interface ArenaLightPresentation {
  readonly hemisphere: Readonly<{
    skyColor: number;
    groundColor: number;
    intensity: number;
  }>;
  readonly key: Readonly<{
    color: number;
    intensity: number;
    position: Vec3;
  }>;
  readonly accents: readonly Readonly<{
    color: number;
    intensity: number;
    distance: number;
    decay: number;
    position: Vec3;
  }>[];
}

export interface ArenaSurfacePresentation {
  readonly color: number;
  readonly roughness: number;
  readonly metalness: number;
  readonly emissive: number;
  readonly emissiveIntensity: number;
  readonly texture?: ArenaTextureSpec;
}

export interface ArenaMarkingPresentation {
  readonly color: number;
  readonly accentColor: number;
  readonly opacity: number;
  readonly boundaryWidth: number;
}

export interface ArenaGeometrySurfacePresentation {
  readonly color: number;
  readonly emissive: number;
  readonly emissiveIntensity: number;
  readonly roughness: number;
  readonly metalness: number;
  readonly opacity: number;
  readonly renderMode?: 'standard' | 'field-bank' | 'continuous-grid' | 'goal-net';
  readonly pattern?: ArenaGeometryPatternPresentation;
}

export type ArenaGeometryPatternPresentation = Readonly<{
  readonly kind: 'honeycomb';
  readonly spacing: number;
  readonly barWidth: number;
}>;

export interface ArenaGeometryPresentation {
  readonly wall: ArenaGeometrySurfacePresentation;
  readonly ceiling: ArenaGeometrySurfacePresentation;
  readonly lowerRamp: ArenaGeometrySurfacePresentation;
  readonly goalInterior: ArenaGeometrySurfacePresentation;
  readonly goalNet: ArenaGeometrySurfacePresentation;
  readonly goalFloor: ArenaGeometrySurfacePresentation;
}

export interface ArenaBoostPadPresentation {
  readonly emissive: number;
  readonly haloColor: number;
  readonly haloOpacity: number;
  readonly inactiveHaloOpacity: number;
  readonly small: Readonly<{
    color: number;
    inactiveColor: number;
    emissiveIntensity: number;
    inactiveEmissiveIntensity: number;
    radius: number;
    height: number;
  }>;
  readonly large: Readonly<{
    color: number;
    emissiveIntensity: number;
    baseRadius: number;
    baseHeight: number;
    markerRadius: number;
    markerHeight: number;
    markerCenterHeight: number;
    rechargeOpacity: number;
  }>;
}

interface ArenaCloudAppearance {
  readonly minScale: number;
  readonly maxScale: number;
  readonly puffsPerCloud: number;
  readonly color: number;
  readonly opacity: number;
  readonly glowColor: number;
  readonly glowOpacity: number;
  readonly driftSpeed: number;
  readonly excludePlayableVolume?: boolean;
}

interface ArenaStructuralCloudSpec {
  readonly seed: number;
  readonly count: number;
  readonly center: Vec3;
  readonly spread: Vec3;
  readonly minScale: number;
  readonly maxScale: number;
}

export type ArenaDecorationSpec =
  | Readonly<ArenaCloudAppearance & {
    kind: 'cloud-layer';
    seed: number;
    count: number;
    center: Vec3;
    spread: Vec3;
  }>
  | Readonly<ArenaCloudAppearance & {
    kind: 'cloud-belt';
    seed: number;
    count: number;
    center: Vec3;
    innerRadius: number;
    outerRadius: number;
    verticalSpread: number;
  }>
  | Readonly<{
    kind: 'halo';
    position: Vec3;
    rotation: Vec3;
    radius: number;
    tubeRadius: number;
    color: number;
    opacity: number;
    spinSpeed: number;
  }>
  | Readonly<{
    kind: 'beam';
    position: Vec3;
    rotation: Vec3;
    size: Vec3;
    color: number;
    emissive: number;
    emissiveIntensity: number;
    opacity: number;
  }>
  | Readonly<{
    kind: 'god-ray';
    position: Vec3;
    rotation: Vec3;
    radius: number;
    height: number;
    color: number;
    opacity: number;
  }>
  | Readonly<{
    kind: 'orb';
    position: Vec3;
    radius: number;
    color: number;
    emissive: number;
    emissiveIntensity: number;
    opacity: number;
    pulseSpeed: number;
  }>
  | Readonly<{
    kind: 'mote-field';
    seed: number;
    count: number;
    center: Vec3;
    spread: Vec3;
    size: number;
    color: number;
    opacity: number;
    twinkleSpeed: number;
  }>
  | Readonly<{
    kind: 'team-goal-monument';
    team: Team;
    position: Vec3;
    rotationYRadians: number;
    height: number;
    depth: number;
    frame: Readonly<{
      color: number;
      emissive: number;
      emissiveIntensity: number;
      roughness: number;
      metalness: number;
      grainColor: number;
      grainStrength: number;
      grainScale: number;
    }>;
    banner: Readonly<{
      width: number;
      height: number;
      topInset: number;
      opacity: number;
    }>;
    halo: Readonly<{
      radius: number;
      tubeRadius: number;
      verticalOffset: number;
      color: number;
      opacity: number;
      wobbleAmount: number;
      spinSpeed: number;
    }>;
    structureClouds: Readonly<{
      color: number;
      emissive: number;
      emissiveIntensity: number;
      roughness: number;
      haloSupport: Readonly<ArenaStructuralCloudSpec>;
    }>;
  }>;

export interface ArenaPresentation {
  readonly id: string;
  readonly visualGeometryUrl: string;
  readonly backgroundLoop: LoopAudioDefinition | null;
  readonly environment: ArenaEnvironmentPresentation;
  readonly lighting: ArenaLightPresentation;
  readonly surface: ArenaSurfacePresentation;
  readonly markings: ArenaMarkingPresentation;
  readonly geometry: ArenaGeometryPresentation;
  readonly boostPads: ArenaBoostPadPresentation;
  readonly decorations: readonly ArenaDecorationSpec[];
}

const CELESTIAL: ArenaPresentation = Object.freeze({
  id: 'celestial',
  visualGeometryUrl: mediaAssetUrl(MEDIA_KEYS.arenaVisualGeometry),
  backgroundLoop: Object.freeze({
    url: mediaAssetUrl(MEDIA_KEYS.arenaCelestialWind), routing: 'global' as const, gain: 0.42,
    fadeInSeconds: 1.2, fadeOutSeconds: 0.8,
  }),
  environment: Object.freeze({
    backgroundColor: 0x78b9e3,
    fogColor: 0xc7e2f2,
    fogDensity: 0.00155,
    exposure: 1.14,
    skyTopColor: 0x2f82c1,
    skyHorizonColor: 0xcfeaff,
    skyBottomColor: 0x8fc4e7,
    sun: Object.freeze({
      direction: { x: -0.42, y: 0.38, z: -0.82 },
      color: 0xfff0c9,
      coreSize: 0.012,
      haloSize: 0.18,
      intensity: 1.08,
    }),
  }),
  lighting: Object.freeze({
    hemisphere: Object.freeze({ skyColor: 0xd9f1ff, groundColor: 0xaab7aa, intensity: 1.9 }),
    key: Object.freeze({ color: 0xffefcf, intensity: 3.3, position: { x: -72, y: 106, z: -88 } }),
    accents: Object.freeze([
      Object.freeze({ color: 0x87d4ff, intensity: 16, distance: 145, decay: 2, position: { x: -46, y: 24, z: -98 } }),
      Object.freeze({ color: 0xffc96c, intensity: 19, distance: 140, decay: 2, position: { x: 48, y: 22, z: 98 } }),
      Object.freeze({ color: 0xffe5a1, intensity: 9, distance: 110, decay: 2, position: { x: 0, y: 42, z: 0 } }),
    ]),
  }),
  surface: Object.freeze({
    color: 0xe3ebdc,
    roughness: 0.86,
    metalness: 0.025,
    emissive: 0x84977e,
    emissiveIntensity: 0.025,
    texture: Object.freeze({ url: mediaAssetUrl(MEDIA_KEYS.arenaCelestialFloor), repeatX: 4, repeatY: 6 }),
  }),
  markings: Object.freeze({
    color: 0xfff4cf,
    accentColor: 0xd5a84a,
    opacity: 0.92,
    boundaryWidth: 0.095,
  }),
  geometry: Object.freeze({
    wall: Object.freeze({
      color: 0x528293,
      emissive: 0x668793,
      emissiveIntensity: 0.03,
      roughness: 0.62,
      metalness: 0.05,
      opacity: 1,
      renderMode: 'continuous-grid',
    }),
    ceiling: Object.freeze({
      color: 0x528293,
      emissive: 0x6d8c97,
      emissiveIntensity: 0.028,
      roughness: 0.64,
      metalness: 0.045,
      opacity: 1,
      renderMode: 'continuous-grid',
    }),
    lowerRamp: Object.freeze({
      color: 0xcbd7cb,
      emissive: 0x83988a,
      emissiveIntensity: 0.05,
      roughness: 0.7,
      metalness: 0.08,
      opacity: 1,
      renderMode: 'field-bank',
    }),
    goalInterior: Object.freeze({
      color: 0xe5e9e6,
      emissive: 0x8faebd,
      emissiveIntensity: 0.075,
      roughness: 0.56,
      metalness: 0.12,
      opacity: 1,
      renderMode: 'field-bank',
    }),
    goalNet: Object.freeze({
      color: 0x528293,
      emissive: 0x000000,
      emissiveIntensity: 0,
      roughness: 1,
      metalness: 0,
      opacity: 1,
      renderMode: 'goal-net',
    }),
    goalFloor: Object.freeze({
      color: 0xe3ebdc,
      emissive: 0x84977e,
      emissiveIntensity: 0.025,
      roughness: 0.86,
      metalness: 0.025,
      opacity: 1,
    }),
  }),
  boostPads: Object.freeze({
    emissive: 0xffbd42,
    haloColor: 0xffd57a,
    haloOpacity: 0.72,
    inactiveHaloOpacity: 0.1,
    small: Object.freeze({
      color: 0xffd777,
      inactiveColor: 0xaeb4b8,
      emissiveIntensity: 2.5,
      inactiveEmissiveIntensity: 0.08,
      radius: 1.03,
      height: 0.08,
    }),
    large: Object.freeze({
      color: 0xffe3a0,
      emissiveIntensity: 3.7,
      baseRadius: 1.34,
      baseHeight: 0.14,
      markerRadius: 0.72,
      markerHeight: 2.65,
      markerCenterHeight: 1.82,
      rechargeOpacity: 0.4,
    }),
  }),
  decorations: Object.freeze([
    Object.freeze({ kind: 'cloud-layer', seed: 1207, count: 52, center: { x: 0, y: -44, z: 0 }, spread: { x: 330, y: 4, z: 400 }, minScale: 9, maxScale: 19, puffsPerCloud: 8, color: 0xf8fcff, opacity: 0.82, glowColor: 0xe8f6ff, glowOpacity: 0.11, driftSpeed: 0.12, excludePlayableVolume: true }),
    Object.freeze({ kind: 'cloud-belt', seed: 2903, count: 38, center: { x: 0, y: 42, z: 0 }, innerRadius: 188, outerRadius: 318, verticalSpread: 70, minScale: 9, maxScale: 25, puffsPerCloud: 7, color: 0xf7fbff, opacity: 0.58, glowColor: 0xe3f4ff, glowOpacity: 0.095, driftSpeed: 0.07, excludePlayableVolume: true }),
    Object.freeze({ kind: 'cloud-belt', seed: 3911, count: 20, center: { x: 0, y: 98, z: 0 }, innerRadius: 218, outerRadius: 350, verticalSpread: 58, minScale: 8, maxScale: 20, puffsPerCloud: 6, color: 0xeaf6ff, opacity: 0.34, glowColor: 0xffffff, glowOpacity: 0.07, driftSpeed: 0.04, excludePlayableVolume: true }),
    Object.freeze({ kind: 'orb', position: { x: -142, y: 112, z: -208 }, radius: 23, color: 0xffeab0, emissive: 0xffc95f, emissiveIntensity: 3.2, opacity: 0.96, pulseSpeed: 0.24 }),
    Object.freeze({ kind: 'beam', position: { x: -93, y: 17, z: -28 }, rotation: { x: 0, y: 0, z: 0 }, size: { x: 0.82, y: 35, z: 0.82 }, color: 0xd9b45b, emissive: 0x8d6624, emissiveIntensity: 0.22, opacity: 0.9 }),
    Object.freeze({ kind: 'beam', position: { x: -93, y: 17, z: 28 }, rotation: { x: 0, y: 0, z: 0 }, size: { x: 0.82, y: 35, z: 0.82 }, color: 0xd9b45b, emissive: 0x8d6624, emissiveIntensity: 0.22, opacity: 0.9 }),
    Object.freeze({ kind: 'beam', position: { x: 93, y: 17, z: -28 }, rotation: { x: 0, y: 0, z: 0 }, size: { x: 0.82, y: 35, z: 0.82 }, color: 0xd9b45b, emissive: 0x8d6624, emissiveIntensity: 0.22, opacity: 0.9 }),
    Object.freeze({ kind: 'beam', position: { x: 93, y: 17, z: 28 }, rotation: { x: 0, y: 0, z: 0 }, size: { x: 0.82, y: 35, z: 0.82 }, color: 0xd9b45b, emissive: 0x8d6624, emissiveIntensity: 0.22, opacity: 0.9 }),
    Object.freeze({
      kind: 'team-goal-monument',
      team: 'blue',
      position: { x: 0, y: 0, z: -126 },
      rotationYRadians: 0,
      height: 31,
      depth: 1.15,
      frame: Object.freeze({ color: 0xa87845, emissive: 0x2d170b, emissiveIntensity: 0.055, roughness: 0.62, metalness: 0.035, grainColor: 0x5b351d, grainStrength: 0.2, grainScale: 2.2 }),
      banner: Object.freeze({ width: 40, height: 16.5, topInset: 3.2, opacity: 0.93 }),
      halo: Object.freeze({ radius: 11.5, tubeRadius: 0.5, verticalOffset: 10.8, color: 0xfff0c5, opacity: 0.84, wobbleAmount: 0.055, spinSpeed: 0.16 }),
      structureClouds: Object.freeze({
        color: 0xf7fbf8,
        emissive: 0xdcebee,
        emissiveIntensity: 0.035,
        roughness: 0.96,
        haloSupport: Object.freeze({ seed: 8117, count: 10, center: { x: 0, y: 38.6, z: 0 }, spread: { x: 22, y: 4.4, z: 9 }, minScale: 3.9, maxScale: 6.1 }),
      }),
    }),
    Object.freeze({
      kind: 'team-goal-monument',
      team: 'orange',
      position: { x: 0, y: 0, z: 126 },
      rotationYRadians: Math.PI,
      height: 31,
      depth: 1.15,
      frame: Object.freeze({ color: 0xa87845, emissive: 0x2d170b, emissiveIntensity: 0.055, roughness: 0.62, metalness: 0.035, grainColor: 0x5b351d, grainStrength: 0.2, grainScale: 2.2 }),
      banner: Object.freeze({ width: 40, height: 16.5, topInset: 3.2, opacity: 0.93 }),
      halo: Object.freeze({ radius: 11.5, tubeRadius: 0.5, verticalOffset: 10.8, color: 0xfff0c5, opacity: 0.84, wobbleAmount: 0.055, spinSpeed: -0.16 }),
      structureClouds: Object.freeze({
        color: 0xf7fbf8,
        emissive: 0xdcebee,
        emissiveIntensity: 0.035,
        roughness: 0.96,
        haloSupport: Object.freeze({ seed: 8219, count: 10, center: { x: 0, y: 38.6, z: 0 }, spread: { x: 22, y: 4.4, z: 9 }, minScale: 3.9, maxScale: 6.1 }),
      }),
    }),
    Object.freeze({ kind: 'mote-field', seed: 7129, count: 260, center: { x: 0, y: 38, z: 0 }, spread: { x: 250, y: 92, z: 300 }, size: 0.2, color: 0xffefb0, opacity: 0.68, twinkleSpeed: 0.68 }),
  ]),
});

export function celestialArenaPresentation(): ArenaPresentation {
  return CELESTIAL;
}

export function validateArenaPresentation(presentation: ArenaPresentation): void {
  if (!presentation.id.trim()) throw new Error('Arena presentation ID must not be empty.');
  if (!presentation.visualGeometryUrl.trim()) throw new Error('Arena visual geometry URL must not be empty.');
  if (presentation.backgroundLoop) {
    if (!presentation.backgroundLoop.url.trim()) throw new Error('Arena background loop URL must not be empty.');
    if (presentation.backgroundLoop.routing !== 'global') throw new Error('Arena background loop must use global routing.');
    nonNegative(presentation.backgroundLoop.gain, 'background loop gain');
    nonNegative(presentation.backgroundLoop.fadeInSeconds, 'background loop fade-in');
    nonNegative(presentation.backgroundLoop.fadeOutSeconds, 'background loop fade-out');
  }
  validateColor(presentation.environment.backgroundColor, 'background');
  validateColor(presentation.environment.fogColor, 'fog');
  validateColor(presentation.environment.skyTopColor, 'sky top');
  validateColor(presentation.environment.skyHorizonColor, 'sky horizon');
  validateColor(presentation.environment.skyBottomColor, 'sky bottom');
  positive(presentation.environment.exposure, 'exposure');
  nonNegative(presentation.environment.fogDensity, 'fog density');
  if (presentation.environment.sun) {
    validateVec3(presentation.environment.sun.direction, 'sun direction');
    if (vectorLength(presentation.environment.sun.direction) <= 0) throw new Error('Arena sun direction must not be zero.');
    validateColor(presentation.environment.sun.color, 'sun');
    positive(presentation.environment.sun.coreSize, 'sun core size');
    positive(presentation.environment.sun.haloSize, 'sun halo size');
    if (presentation.environment.sun.haloSize < presentation.environment.sun.coreSize) {
      throw new Error('Arena sun halo size must be >= core size.');
    }
    nonNegative(presentation.environment.sun.intensity, 'sun intensity');
  }
  validateColor(presentation.lighting.hemisphere.skyColor, 'hemisphere sky');
  validateColor(presentation.lighting.hemisphere.groundColor, 'hemisphere ground');
  nonNegative(presentation.lighting.hemisphere.intensity, 'hemisphere intensity');
  validateColor(presentation.lighting.key.color, 'key light');
  nonNegative(presentation.lighting.key.intensity, 'key light intensity');
  validateVec3(presentation.lighting.key.position, 'key light position');
  for (const accent of presentation.lighting.accents) {
    validateColor(accent.color, 'accent light');
    nonNegative(accent.intensity, 'accent light intensity');
    positive(accent.distance, 'accent light distance');
    positive(accent.decay, 'accent light decay');
    validateVec3(accent.position, 'accent light position');
  }
  validateSurface(presentation.surface, 'surface');
  validateColor(presentation.markings.color, 'marking');
  validateColor(presentation.markings.accentColor, 'marking accent');
  unit(presentation.markings.opacity, 'marking opacity');
  positive(presentation.markings.boundaryWidth, 'marking boundary width');
  validateGeometrySurface(presentation.geometry.wall, 'wall');
  validateGeometrySurface(presentation.geometry.ceiling, 'ceiling');
  validateGeometrySurface(presentation.geometry.lowerRamp, 'lower ramp');
  validateGeometrySurface(presentation.geometry.goalInterior, 'goal interior');
  validateGeometrySurface(presentation.geometry.goalNet, 'goal net');
  validateGeometrySurface(presentation.geometry.goalFloor, 'goal floor');
  validateColor(presentation.boostPads.emissive, 'boost emissive');
  validateColor(presentation.boostPads.haloColor, 'boost halo');
  unit(presentation.boostPads.haloOpacity, 'boost halo opacity');
  unit(presentation.boostPads.inactiveHaloOpacity, 'inactive boost halo opacity');
  validateColor(presentation.boostPads.small.color, 'small boost');
  validateColor(presentation.boostPads.small.inactiveColor, 'inactive small boost');
  nonNegative(presentation.boostPads.small.emissiveIntensity, 'small boost emissive intensity');
  nonNegative(presentation.boostPads.small.inactiveEmissiveIntensity, 'inactive small boost emissive intensity');
  positive(presentation.boostPads.small.radius, 'small boost radius');
  positive(presentation.boostPads.small.height, 'small boost height');
  validateColor(presentation.boostPads.large.color, 'large boost');
  nonNegative(presentation.boostPads.large.emissiveIntensity, 'large boost emissive intensity');
  positive(presentation.boostPads.large.baseRadius, 'large boost base radius');
  positive(presentation.boostPads.large.baseHeight, 'large boost base height');
  positive(presentation.boostPads.large.markerRadius, 'large boost marker radius');
  positive(presentation.boostPads.large.markerHeight, 'large boost marker height');
  positive(presentation.boostPads.large.markerCenterHeight, 'large boost marker center height');
  unit(presentation.boostPads.large.rechargeOpacity, 'large boost recharge opacity');
  for (const decoration of presentation.decorations) validateDecoration(decoration);
}

function validateSurface(surface: ArenaSurfacePresentation, label: string): void {
  validateColor(surface.color, label);
  validateColor(surface.emissive, `${label} emissive`);
  unit(surface.roughness, `${label} roughness`);
  unit(surface.metalness, `${label} metalness`);
  nonNegative(surface.emissiveIntensity, `${label} emissive intensity`);
  if (!surface.texture) return;
  if (!surface.texture.url.trim()) throw new Error(`Arena ${label} texture URL must not be empty.`);
  positive(surface.texture.repeatX, `${label} texture repeatX`);
  positive(surface.texture.repeatY, `${label} texture repeatY`);
}

function validateGeometrySurface(surface: ArenaGeometrySurfacePresentation, label: string): void {
  validateColor(surface.color, label);
  validateColor(surface.emissive, `${label} emissive`);
  nonNegative(surface.emissiveIntensity, `${label} emissive intensity`);
  unit(surface.roughness, `${label} roughness`);
  unit(surface.metalness, `${label} metalness`);
  unit(surface.opacity, `${label} opacity`);
  const renderMode = surface.renderMode ?? 'standard';
  if (
    renderMode !== 'standard'
    && renderMode !== 'field-bank'
    && renderMode !== 'continuous-grid'
    && renderMode !== 'goal-net'
  ) {
    throw new Error(`Arena ${label} render mode is unsupported.`);
  }
  if (renderMode === 'continuous-grid' || renderMode === 'goal-net') {
    if (surface.pattern) throw new Error(`Arena ${label} transparent grid cannot also use a cutout pattern.`);
    if (surface.opacity !== 1) throw new Error(`Arena ${label} transparent grid opacity is shader-controlled.`);
    return;
  }
  if (!surface.pattern) return;
  if (surface.pattern.kind !== 'honeycomb') {
    throw new Error(`Arena ${label} pattern kind is unsupported.`);
  }
  positive(surface.pattern.spacing, `${label} honeycomb spacing`);
  positive(surface.pattern.barWidth, `${label} honeycomb bar width`);
  if (surface.pattern.barWidth >= surface.pattern.spacing * 0.5) {
    throw new Error(`Arena ${label} honeycomb bar width must be less than half its spacing.`);
  }
  if (surface.opacity < 1) {
    throw new Error(`Arena ${label} honeycomb surface must be opaque; the pattern provides its own cutout.`);
  }
}

function validateDecoration(decoration: ArenaDecorationSpec): void {
  switch (decoration.kind) {
    case 'cloud-layer':
      validateCloudAppearance(decoration);
      positiveInteger(decoration.count, 'cloud count');
      validateVec3(decoration.center, 'cloud center');
      validatePositiveVec3(decoration.spread, 'cloud spread');
      return;
    case 'cloud-belt':
      validateCloudAppearance(decoration);
      positiveInteger(decoration.count, 'cloud belt count');
      validateVec3(decoration.center, 'cloud belt center');
      positive(decoration.innerRadius, 'cloud belt inner radius');
      positive(decoration.outerRadius, 'cloud belt outer radius');
      if (decoration.outerRadius <= decoration.innerRadius) throw new Error('Arena cloud belt outer radius must be > inner radius.');
      positive(decoration.verticalSpread, 'cloud belt vertical spread');
      return;
    case 'halo':
      positive(decoration.radius, 'halo radius');
      positive(decoration.tubeRadius, 'halo tube radius');
      validateVec3(decoration.position, 'halo position');
      validateVec3(decoration.rotation, 'halo rotation');
      validateColor(decoration.color, 'halo');
      unit(decoration.opacity, 'halo opacity');
      finite(decoration.spinSpeed, 'halo spin speed');
      return;
    case 'beam':
      validateVec3(decoration.position, 'beam position');
      validateVec3(decoration.rotation, 'beam rotation');
      positive(decoration.size.x, 'beam size.x');
      positive(decoration.size.y, 'beam size.y');
      positive(decoration.size.z, 'beam size.z');
      validateColor(decoration.color, 'beam');
      validateColor(decoration.emissive, 'beam emissive');
      nonNegative(decoration.emissiveIntensity, 'beam emissive intensity');
      unit(decoration.opacity, 'beam opacity');
      return;
    case 'god-ray':
      validateVec3(decoration.position, 'god ray position');
      validateVec3(decoration.rotation, 'god ray rotation');
      positive(decoration.radius, 'god ray radius');
      positive(decoration.height, 'god ray height');
      validateColor(decoration.color, 'god ray');
      unit(decoration.opacity, 'god ray opacity');
      return;
    case 'orb':
      validateVec3(decoration.position, 'orb position');
      positive(decoration.radius, 'orb radius');
      validateColor(decoration.color, 'orb');
      validateColor(decoration.emissive, 'orb emissive');
      nonNegative(decoration.emissiveIntensity, 'orb emissive intensity');
      unit(decoration.opacity, 'orb opacity');
      nonNegative(decoration.pulseSpeed, 'orb pulse speed');
      return;
    case 'mote-field':
      positiveInteger(decoration.count, 'mote count');
      validateVec3(decoration.center, 'mote center');
      validatePositiveVec3(decoration.spread, 'mote spread');
      positive(decoration.size, 'mote size');
      validateColor(decoration.color, 'mote');
      unit(decoration.opacity, 'mote opacity');
      nonNegative(decoration.twinkleSpeed, 'mote twinkle speed');
      return;
    case 'team-goal-monument':
      if (decoration.team !== 'blue' && decoration.team !== 'orange') {
        throw new Error('Arena team goal monument team must be blue or orange.');
      }
      validateVec3(decoration.position, 'team goal monument position');
      finite(decoration.rotationYRadians, 'team goal monument rotation');
      positive(decoration.height, 'team goal monument height');
      positive(decoration.depth, 'team goal monument depth');
      validateColor(decoration.frame.color, 'team goal monument frame');
      validateColor(decoration.frame.emissive, 'team goal monument frame emissive');
      nonNegative(decoration.frame.emissiveIntensity, 'team goal monument frame emissive intensity');
      unit(decoration.frame.roughness, 'team goal monument frame roughness');
      unit(decoration.frame.metalness, 'team goal monument frame metalness');
      validateColor(decoration.frame.grainColor, 'team goal monument frame grain');
      unit(decoration.frame.grainStrength, 'team goal monument frame grain strength');
      positive(decoration.frame.grainScale, 'team goal monument frame grain scale');
      positive(decoration.banner.width, 'team goal monument banner width');
      positive(decoration.banner.height, 'team goal monument banner height');
      nonNegative(decoration.banner.topInset, 'team goal monument banner top inset');
      if (decoration.banner.height + decoration.banner.topInset >= decoration.height) {
        throw new Error('Arena team goal monument banner must fit below its mount height.');
      }
      unit(decoration.banner.opacity, 'team goal monument banner opacity');
      positive(decoration.halo.radius, 'team goal monument halo radius');
      positive(decoration.halo.tubeRadius, 'team goal monument halo tube radius');
      positive(decoration.halo.verticalOffset, 'team goal monument halo vertical offset');
      validateColor(decoration.halo.color, 'team goal monument halo');
      unit(decoration.halo.opacity, 'team goal monument halo opacity');
      nonNegative(decoration.halo.wobbleAmount, 'team goal monument halo wobble amount');
      finite(decoration.halo.spinSpeed, 'team goal monument halo spin speed');
      validateColor(decoration.structureClouds.color, 'team goal monument structure cloud');
      validateColor(decoration.structureClouds.emissive, 'team goal monument structure cloud emissive');
      nonNegative(decoration.structureClouds.emissiveIntensity, 'team goal monument structure cloud emissive intensity');
      unit(decoration.structureClouds.roughness, 'team goal monument structure cloud roughness');
      validateStructuralCloud(decoration.structureClouds.haloSupport, 'halo support');
      return;
  }
}

function validateStructuralCloud(
  cloud: Readonly<ArenaStructuralCloudSpec>,
  label: string,
): void {
  if (!Number.isSafeInteger(cloud.seed)) throw new Error(`Arena team goal monument ${label} cloud seed must be a safe integer.`);
  positiveInteger(cloud.count, `team goal monument ${label} cloud count`);
  validateVec3(cloud.center, `team goal monument ${label} cloud center`);
  validatePositiveVec3(cloud.spread, `team goal monument ${label} cloud spread`);
  positive(cloud.minScale, `team goal monument ${label} cloud minScale`);
  positive(cloud.maxScale, `team goal monument ${label} cloud maxScale`);
  if (cloud.maxScale < cloud.minScale) {
    throw new Error(`Arena team goal monument ${label} cloud maxScale must be >= minScale.`);
  }
}

function validateCloudAppearance(cloud: ArenaCloudAppearance): void {
  positive(cloud.minScale, 'cloud minScale');
  positive(cloud.maxScale, 'cloud maxScale');
  if (cloud.maxScale < cloud.minScale) throw new Error('Arena cloud maxScale must be >= minScale.');
  positiveInteger(cloud.puffsPerCloud, 'cloud puffs per cloud');
  validateColor(cloud.color, 'cloud');
  validateColor(cloud.glowColor, 'cloud glow');
  unit(cloud.opacity, 'cloud opacity');
  unit(cloud.glowOpacity, 'cloud glow opacity');
  nonNegative(cloud.driftSpeed, 'cloud drift speed');
  if (cloud.excludePlayableVolume !== undefined && typeof cloud.excludePlayableVolume !== 'boolean') {
    throw new Error('Arena cloud excludePlayableVolume must be boolean when provided.');
  }
}

function validateColor(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0 || value > 0xffffff) {
    throw new Error(`Arena ${label} color must be an RGB integer.`);
  }
}

function unit(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0 || value > 1) throw new Error(`Arena ${label} must be between 0 and 1.`);
}

function positive(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) throw new Error(`Arena ${label} must be a positive finite number.`);
}

function nonNegative(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0) throw new Error(`Arena ${label} must be a non-negative finite number.`);
}

function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`Arena ${label} must be finite.`);
}

function validateVec3(value: Vec3, label: string): void {
  finite(value.x, `${label}.x`);
  finite(value.y, `${label}.y`);
  finite(value.z, `${label}.z`);
}

function validatePositiveVec3(value: Vec3, label: string): void {
  positive(value.x, `${label}.x`);
  positive(value.y, `${label}.y`);
  positive(value.z, `${label}.z`);
}

function vectorLength(value: Vec3): number {
  return Math.hypot(value.x, value.y, value.z);
}

function positiveInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error(`Arena ${label} must be a positive integer.`);
}
